/**
 * Service für den Support-Chat (asynchrone Tickets in Chat-Optik).
 * User sehen/schreiben nur eigene Tickets (RLS); Admins alles.
 * Keine Edge Functions — reines RLS-gesichertes CRUD.
 */
import { supabase } from '@/lib/supabase';
import { SupportMessage, SupportTicket, SupportTicketStatus } from '@/types';

interface TicketRow {
  id: string;
  user_id: string;
  status: SupportTicketStatus;
  created_at: string;
  updated_at: string;
}

interface MessageRow {
  id: string;
  ticket_id: string;
  sender_role: 'user' | 'admin';
  body: string;
  read_by_user: boolean;
  read_by_admin: boolean;
  created_at: string;
}

function mapTicket(row: TicketRow): SupportTicket {
  return {
    id: row.id,
    userId: row.user_id,
    status: row.status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapMessage(row: MessageRow): SupportMessage {
  return {
    id: row.id,
    ticketId: row.ticket_id,
    senderRole: row.sender_role,
    body: row.body,
    readByUser: row.read_by_user,
    readByAdmin: row.read_by_admin,
    createdAt: row.created_at,
  };
}

export interface SupportThread {
  ticket: SupportTicket;
  messages: SupportMessage[];
}

// Realtime-Payloads (Migration 122) liefern rohe snake_case-Rows — Mapper für Subscriber exportiert.
export type SupportMessageRow = MessageRow;
export const mapSupportMessageRow = mapMessage;

// ── User-facing ─────────────────────────────────────────────────────────────

/** Jüngstes Ticket des Users samt Nachrichten (auch geschlossene — der Thread bleibt lesbar). */
export async function fetchLatestThread(): Promise<SupportThread | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: ticketRow, error: ticketError } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (ticketError) { console.error('[Support] fetchLatestThread ticket', ticketError); throw ticketError; }
  if (!ticketRow) return null;
  const { data: msgRows, error: msgError } = await supabase
    .from('support_messages')
    .select('*')
    .eq('ticket_id', ticketRow.id)
    .order('created_at', { ascending: true });
  if (msgError) { console.error('[Support] fetchLatestThread messages', msgError); throw msgError; }
  return { ticket: mapTicket(ticketRow), messages: (msgRows ?? []).map(mapMessage) };
}

/**
 * Sendet eine User-Nachricht. `ticketId = null` (kein offenes Ticket) legt zuerst
 * ein neues Ticket an. Gibt zurück, ob ein Ticket erstellt wurde (fürs Tracking).
 */
export async function sendUserMessage(
  ticketId: string | null,
  body: string,
): Promise<{ ticketId: string; message: SupportMessage; createdTicket: boolean }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  let targetTicketId = ticketId;
  let createdTicket = false;
  if (!targetTicketId) {
    const { data, error } = await supabase
      .from('support_tickets')
      .insert({ user_id: user.id })
      .select('id')
      .single();
    if (error) { console.error('[Support] createTicket', error); throw error; }
    targetTicketId = data.id as string;
    createdTicket = true;
  }

  const { data: msgRow, error: msgError } = await supabase
    .from('support_messages')
    .insert({
      ticket_id: targetTicketId,
      sender_role: 'user',
      sender_id: user.id,
      body: body.trim(),
      read_by_user: true, // eigene Nachricht ist für den Absender gelesen
    })
    .select('*')
    .single();
  if (msgError) { console.error('[Support] sendUserMessage', msgError); throw msgError; }
  return { ticketId: targetTicketId, message: mapMessage(msgRow), createdTicket };
}

export async function markThreadReadByUser(ticketId: string): Promise<void> {
  const { error } = await supabase
    .from('support_messages')
    .update({ read_by_user: true })
    .eq('ticket_id', ticketId)
    .eq('sender_role', 'admin')
    .eq('read_by_user', false);
  if (error) console.error('[Support] markThreadReadByUser', error); // non-fatal
}

/**
 * Ungelesene Admin-Antworten über alle eigenen Tickets. Explizit auf eigene Tickets
 * gescoped (inner join), weil Admin-RLS sonst Nachrichten fremder Tickets mitzählt.
 */
export async function fetchUserUnreadCount(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;
  const { count, error } = await supabase
    .from('support_messages')
    .select('id, ticket:support_tickets!inner(user_id)', { count: 'exact', head: true })
    .eq('ticket.user_id', user.id)
    .eq('sender_role', 'admin')
    .eq('read_by_user', false);
  if (error) { console.error('[Support] fetchUserUnreadCount', error); return 0; }
  return count ?? 0;
}

// ── Admin ───────────────────────────────────────────────────────────────────

export interface AdminTicket extends SupportTicket {
  userEmail: string | null;
  userName: string | null;
  unreadCount: number;
}

export async function fetchTicketsAdmin(): Promise<AdminTicket[]> {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*, profiles:user_id(email, full_name)')
    .order('updated_at', { ascending: false });
  if (error) { console.error('[Support] fetchTicketsAdmin', error); throw error; }

  // Unread-Counts in einem zweiten Query (eine Roundtrip-Runde statt N)
  const { data: unread, error: unreadError } = await supabase
    .from('support_messages')
    .select('ticket_id')
    .eq('sender_role', 'user')
    .eq('read_by_admin', false);
  if (unreadError) console.error('[Support] fetchTicketsAdmin unread', unreadError);
  const unreadByTicket = new Map<string, number>();
  for (const row of unread ?? []) {
    unreadByTicket.set(row.ticket_id, (unreadByTicket.get(row.ticket_id) ?? 0) + 1);
  }

  type JoinedRow = TicketRow & { profiles: { email: string | null; full_name: string | null } | null };
  return ((data ?? []) as JoinedRow[]).map((row) => ({
    ...mapTicket(row),
    userEmail: row.profiles?.email ?? null,
    userName: row.profiles?.full_name ?? null,
    unreadCount: unreadByTicket.get(row.id) ?? 0,
  }));
}

export async function fetchTicketMessagesAdmin(ticketId: string): Promise<SupportMessage[]> {
  const { data, error } = await supabase
    .from('support_messages')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) { console.error('[Support] fetchTicketMessagesAdmin', error); throw error; }
  return (data ?? []).map(mapMessage);
}

/** Antwortet als Support und setzt das Ticket auf „beantwortet". */
export async function sendAdminReply(ticketId: string, body: string): Promise<SupportMessage> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('support_messages')
    .insert({
      ticket_id: ticketId,
      sender_role: 'admin',
      sender_id: user.id,
      body: body.trim(),
      read_by_admin: true,
    })
    .select('*')
    .single();
  if (error) { console.error('[Support] sendAdminReply', error); throw error; }
  await setTicketStatus(ticketId, 'answered');
  return mapMessage(data);
}

export async function setTicketStatus(ticketId: string, status: SupportTicketStatus): Promise<void> {
  const { error } = await supabase
    .from('support_tickets')
    .update({ status })
    .eq('id', ticketId);
  if (error) { console.error('[Support] setTicketStatus', error); throw error; }
}

export async function markThreadReadByAdmin(ticketId: string): Promise<void> {
  const { error } = await supabase
    .from('support_messages')
    .update({ read_by_admin: true })
    .eq('ticket_id', ticketId)
    .eq('sender_role', 'user')
    .eq('read_by_admin', false);
  if (error) console.error('[Support] markThreadReadByAdmin', error); // non-fatal
}

export async function fetchAdminUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from('support_messages')
    .select('id', { count: 'exact', head: true })
    .eq('sender_role', 'user')
    .eq('read_by_admin', false);
  if (error) { console.error('[Support] fetchAdminUnreadCount', error); return 0; }
  return count ?? 0;
}
